import React, { useState } from "react";
import Box from "@mui/material/Box";
import { s3baseUrl } from "src/config/config";
import { useNavigate } from "react-router-dom";
import { useChat } from "src/Hooks/ChatContext";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { gr } from "src/design-system/tokens";
import icons from "src/design-system/icons";
import GrIcon from "src/design-system/components/GrIcon";
import { grCleanName } from "src/design-system/components/GrChatListRow";
import GrIconButton from "src/design-system/components/GrIconButton";
import { GrMenu, GrMenuItem } from "src/design-system/components/GrMenu";
import GrCard from "src/design-system/components/GrCard";
import GrAvatar from "src/design-system/components/GrAvatar";
import GrBadge from "src/design-system/components/GrBadge";
import GrButton from "src/design-system/components/GrButton";
import GrDialog from "src/design-system/components/GrDialog";
import GrConfirmDialog from "src/design-system/components/GrConfirmDialog";
import GrEmptyState from "src/design-system/components/GrEmptyState";

// Person card: avatar, name and badge centred; actions in one full-width row at the bottom.
const personCardSx = {
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: gr.space[2],
  pt: gr.space[5],
};
const personNameSx = {
  maxWidth: "100%",
  // Same casing as the chat list and New Message drawer ("zohaib test" → "Zohaib Test").
  textTransform: "capitalize",
  mt: gr.space[1],
  fontSize: gr.font.size.md,
  fontWeight: gr.font.weight.semibold,
  color: gr.color.ink,
  lineHeight: gr.font.lineHeight.tight,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const personMetaSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexWrap: "wrap",
  gap: gr.space[2],
};
const personActionsSx = {
  width: "100%",
  mt: "auto",
  pt: gr.space[3],
  display: "flex",
  gap: gr.space[2],
};

function FriendMoreMenu({ name, partnerLabel, onPartner, onUnfriend }) {
  const [anchor, setAnchor] = useState(null);
  return (
    <Box sx={{ position: "absolute", top: gr.space[2], right: gr.space[2] }}>
      <GrIconButton
        variant="ghost"
        size="sm"
        aria-label={`More actions for ${name}`}
        aria-haspopup="menu"
        // Light fill so the ⋯ reads as a button; tint on hover.
        sx={{
          background: gr.color.bg,
          color: gr.color.ink2,
          "&:hover": { background: gr.color.tint, color: gr.color.brandDark },
        }}
        onClick={(e) => setAnchor(e.currentTarget)}
      >
        <GrIcon icon={icons.more} />
      </GrIconButton>
      <GrMenu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {/* Partner action: neutral text + icon. Unfriend: the most destructive one, red, last, after a divider. */}
        <GrMenuItem
          onClick={() => {
            setAnchor(null);
            onPartner();
          }}
        >
          <GrIcon
            icon={
              partnerLabel === "Add Partner"
                ? icons.personAdd
                : icons.personRemove
            }
          />
          {partnerLabel}
        </GrMenuItem>
        <Box
          component="li"
          role="separator"
          sx={{ my: gr.space[1], borderTop: `1px solid ${gr.color.line2}` }}
        />
        <GrMenuItem
          danger
          onClick={() => {
            setAnchor(null);
            onUnfriend();
          }}
        >
          <GrIcon icon={icons.personRemove} />
          Unfriend
        </GrMenuItem>
      </GrMenu>
    </Box>
  );
}

const GymMembers = ({
  friends,
  handleAddFriend = () => {},
  type,
  hasMore,
  isLoadingMore,
  lastBookElementRef,
  loadMoreData,
  selectedFriend,
}) => {
  const navigate = useNavigate();
  const { handleAddChat } = useChat();
  const [openUnfriend, setOpenUnfriend] = useState(false);
  const [openPartner, setOpenPartner] = useState(false);
  const [openPartner1, setOpenPartner1] = useState(false);
  const [userName, setUserName] = useState({});
  const [unfriendId, setUnfriendId] = useState("");
  const [partnerId, setpartnerId] = useState("");
  const [openAbout, setOpenAbout] = useState(false);
  const { userInfo } = useContentSetting();

  let friends_list = friends;

  const handleOpenAbout = (value) => {
    setOpenAbout(true);
    setUserName(value);
  };

  const handleAgreeUnfriend = () => {
    handleAddFriend(unfriendId, "unfriendRequest");
    setOpenUnfriend(false);
  };

  const handleAgreePartner = () => {
    handleAddFriend(partnerId, "unfriend_partner_Request", "partner");
    setOpenPartner(false);
  };

  const handleAgreePartner1 = () => {
    handleAddFriend(partnerId, "unfriend_partner_Request");
    // Bug fix (Mohsin approved): this dialog is openPartner1, so close that one (was setOpenPartner).
    setOpenPartner1(false);
  };

  const handleAddPartner = (value) => {
    let type = "send_accountability_request";
    if (value.request_status === "accepted_accountability_partner") {
      type = "unfriend_partner_Request";
    } else if (
      value.request_status === "pending_accountability_partner" &&
      value.senderId === userInfo._id
    ) {
      type = "cancel_friend_request";
    }

    if (type === "unfriend_partner_Request") {
      setpartnerId(value._id);
      setOpenPartner(true);
    } else {
      handleAddFriend(value._id, type, "partner");
    }
  };

  const handleAddPartnerOpen = (id) => {
    setpartnerId(id);
    setOpenPartner1(true);
  };

  const handleOpenUnfriend = (id) => {
    setUnfriendId(id);
    setOpenUnfriend(true);
  };

  const handleChat = (id) => {
    navigate(`/chat/?chat_id=${id}`);
    handleAddChat(id);
  };

  // Confirmation dialog: quiet Cancel + one primary. Same open/close/agree wiring as before.
  // Unfriend / remove partner: destructive confirm (same wording as before).
  const renderConfirm = (title, open, setOpen, handleAgree, agreeLabel) => (
    <GrConfirmDialog
      open={open}
      onClose={() => setOpen(false)}
      onConfirm={handleAgree}
      title={title}
      confirmLabel={agreeLabel}
      destructive
      // People are removed, not deleted: person icon instead of the trash can.
      icon={icons.personRemove}
    />
  );

  return (
    <Box>
      {friends_list && friends_list.length > 0 ? (
        <>
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              listStyle: "none",
              display: "grid",
              gap: gr.space[4],
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
            }}
          >
            {friends_list.map((item, index) => {
              // grCleanName drops "undefined"/"null" parts of names missing on the server.
              const fullName = grCleanName(
                item.first_name + " " + item.last_name
              );
              const isSelected = selectedFriend.includes(item._id);
              const isPartner =
                item.request_status === "pending_accountability_partner" ||
                item.request_status === "accepted_accountability_partner";
              const showPartnerBadge =
                (type === "sent_request" ||
                  type === "received_request" ||
                  type === "friends_list") &&
                isPartner;
              // Same handler as before for the card's main button (labels unchanged).
              const handleMain = () => {
                if (
                  selectedFriend.includes(item._id) ||
                  type === "sent_request"
                ) {
                  handleAddFriend(item._id, "cancel_friend_request");
                } else if (type === "received_request") {
                  handleAddFriend(
                    item._id,
                    item.request_status === "pending"
                      ? "accept_request"
                      : "accept_accountability_request"
                  );
                } else if (type === "gym_rite") {
                  handleAddFriend(item._id, "send_request");
                } else if (type === "friends_list") {
                  handleOpenUnfriend(item._id);
                } else if (type === "accountability_partner_list") {
                  handleAddPartnerOpen(item._id);
                }
              };
              const mainLabel =
                type === "gym_rite"
                  ? selectedFriend.includes(item._id)
                    ? "Cancel Request"
                    : "Add Friend"
                  : type === "sent_request"
                  ? "Cancel Request"
                  : type === "received_request"
                  ? selectedFriend.includes(item._id)
                    ? "Request Accepted"
                    : "Accept Request"
                  : type === "accountability_partner_list"
                  ? "Remove Partner"
                  : "Unfriend";
              const partnerLabel =
                item.request_status === "accepted_accountability_partner"
                  ? "Remove Partner"
                  : item.request_status === "pending_accountability_partner" &&
                    item.senderId === userInfo._id
                  ? "Cancel AP Request"
                  : "Add Partner";
              // Visual weight only: Accept (rare, key action) = soft fill; Add Friend (on every card) = teal
              // outline; cancel / remove / reject = danger (red text outline).
              const mainVariant =
                type === "received_request"
                  ? "soft"
                  : type === "gym_rite" && !isSelected
                  ? "outline"
                  : "danger";

              return (
                <GrCard
                  as="li"
                  padding="sm"
                  key={item._id ?? index}
                  sx={personCardSx}
                >
                  {type === "friends_list" ? (
                    // Unfriend + partner actions in the ⋯ menu so they cannot be clicked by mistake.
                    <FriendMoreMenu
                      name={fullName}
                      partnerLabel={partnerLabel}
                      onPartner={() => handleAddPartner(item)}
                      onUnfriend={handleMain}
                    />
                  ) : null}
                  <GrAvatar
                    src={s3baseUrl + item.profile_image}
                    name={fullName}
                    size="lg"
                  />
                  <Box component="span" sx={personNameSx} title={fullName}>
                    {fullName}
                  </Box>
                  {/* Only when there is a badge or About Me, so cards without them have no empty gap. */}
                  {showPartnerBadge || item.member_biography ? (
                    <Box sx={personMetaSx}>
                      {showPartnerBadge ? (
                        <GrBadge tone="brand">Accountability Partner</GrBadge>
                      ) : null}
                      {item.member_biography && (
                        <GrButton
                          variant="link"
                          size="sm"
                          onClick={() => {
                            handleOpenAbout(item);
                          }}
                        >
                          About Me
                        </GrButton>
                      )}
                    </Box>
                  ) : null}

                  <Box sx={personActionsSx}>
                    {type === "friends_list" ? (
                      <GrButton
                        onClick={() => handleChat(item._id)}
                        variant="outline"
                        size="sm"
                        fullWidth
                        startIcon={<GrIcon icon={icons.chat} />}
                      >
                        Chat
                      </GrButton>
                    ) : (
                      <>
                        {!isSelected && type === "received_request" && (
                          <GrButton
                            variant="danger"
                            size="sm"
                            fullWidth
                            onClick={() =>
                              handleAddFriend(item._id, "reject_request")
                            }
                          >
                            Reject
                          </GrButton>
                        )}
                        <GrButton
                          onClick={handleMain}
                          variant={mainVariant}
                          disabled={
                            selectedFriend.includes(item._id) &&
                            type === "received_request"
                          }
                          size="sm"
                          fullWidth
                          startIcon={
                            type === "gym_rite" && !isSelected ? (
                              <GrIcon icon={icons.personAdd} />
                            ) : type === "received_request" ? (
                              <GrIcon icon={icons.check} />
                            ) : undefined
                          }
                        >
                          {mainLabel}
                        </GrButton>
                      </>
                    )}
                  </Box>
                </GrCard>
              );
            })}
          </Box>
          {hasMore ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: gr.space[6],
              }}
            >
              <GrButton
                variant="text"
                ref={lastBookElementRef}
                onClick={loadMoreData}
                id="load-more-feed"
                loading={isLoadingMore}
              >
                Load More
              </GrButton>
            </Box>
          ) : (
            ""
          )}
        </>
      ) : (
        <GrCard padding="sm">
          <GrEmptyState
            compact
            icon={<GrIcon icon={icons.people} />}
            title={
              type === "received_request"
                ? "No friend requests yet"
                : `${
                    type === "gym_rite"
                      ? "Gym Members"
                      : type === "sent_request"
                      ? "Sent Request"
                      : type === "accountability_partner_list"
                      ? "Accountability Partner"
                      : "Friends"
                  } Not Found`
            }
          />
        </GrCard>
      )}
      {renderConfirm(
        "Are you sure you want to unfriend this user?",
        openUnfriend,
        setOpenUnfriend,
        handleAgreeUnfriend,
        "Agree"
      )}
      {renderConfirm(
        "Are you sure you want to remove your accountability partner?",
        openPartner,
        setOpenPartner,
        handleAgreePartner,
        "Agree"
      )}
      {renderConfirm(
        "Are you sure you want to remove your accountability partner?",
        openPartner1,
        setOpenPartner1,
        handleAgreePartner1,
        "Agree"
      )}
      <GrDialog
        open={openAbout}
        onClose={() => setOpenAbout(false)}
        title={
          grCleanName(userName.first_name + " " + userName.last_name) ||
          "About Me"
        }
        actions={
          <GrButton variant="quiet" onClick={() => setOpenAbout(false)}>
            Close
          </GrButton>
        }
      >
        <Box component="p" sx={{ m: 0, whiteSpace: "pre-line" }}>
          {userName.member_biography}
        </Box>
      </GrDialog>
    </Box>
  );
};

export default GymMembers;
